import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ampli } from '@/ampli';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { tokenFormatter } from '@/lib/formatting';

import {
  copyTxLinkInClipboard,
  getSharePageURL,
  shareTxOnX,
} from '@/components/TransactionDetails/share';
import { ShareTxBottomPanelModal } from '@/components/TransactionDetails/ShareTxBottomPanelModal';
import { TriggerOrders, type TriggerOrdersProps } from '@/components/TriggerOrders/TriggerOrders';

import { CHAIN_IDS } from '@/constants/chain';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useTransactionConfetti } from '@/hooks/useTransactionConfetti';

import { useTransactionStore } from '@/store/transaction';
import { getTxHash } from '@/store/transaction/utils';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';
import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ArrowDownIcon } from '@/ui/Icons/ArrowDownIcon';
import { CheckCircleIcon } from '@/ui/Icons/CheckCircleIcon';
import { CopyIcon } from '@/ui/Icons/CopyIcon';
import { ModalBadge } from '@/ui/Icons/ModalBadge';
import { TwitterIcon } from '@/ui/Icons/TwitterIcon';

import { getBlockExplorerTransactionURL } from '@/utils/blockExplorers';
import { classNames } from '@/utils/classnames';
import { ChainHash } from '@/utils/models';
import { isSameTokenAddress } from '@/utils/normalizeAddress';
import { routes } from '@/utils/routes';

import { AlertSuccessGradient } from '../../Alerts/shared';
import { usePostSwapSellAmount } from './usePostSwapSellAmount';
import { isPostSwapTriggerOrderEligible } from './utils';

const { base, wide, item, icon } = popover();

const MOVE_DOWN = 'animate-[moveDown_500ms_cubic-bezier(0.4,0,0,1)_forwards]';
const FADE_IN_AND_GROW = 'animate-[fadeInAndGrowAmount_500ms_cubic-bezier(0.4,0,0,1)_forwards]';
const FADE_IN = 'animate-[fadeIn_500ms_cubic-bezier(0.4,0,0,1)]';

interface ShareTxDropdownProps {
  trigger: React.ReactNode;
  isOpen: boolean;
  handleOpenChange: (open: boolean) => void;
  onShareOnX: () => void;
  onCopyLink: () => void;
}

const ShareTxDropdown = ({
  trigger,
  isOpen,
  handleOpenChange,
  onShareOnX,
  onCopyLink,
}: ShareTxDropdownProps) => {
  const [hasCopied, setHasCopied] = useState(false);

  const onCopy = (event: Event) => {
    event.preventDefault();
    onCopyLink();
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 2000);
  };

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className={`${base()} ${wide()} p-0`}>
        <ul className="text-token-gray-800 p-[6px]">
          <DropdownMenu.Item className={item()} onSelect={onShareOnX}>
            <TwitterIcon className={icon()} />
            Share on X.com
          </DropdownMenu.Item>
          <DropdownMenu.Item className={item()} onSelect={onCopy}>
            {hasCopied ? (
              <>
                <CheckCircleIcon className="mr-2" /> Copied
              </>
            ) : (
              <>
                <CopyIcon className="mr-2" /> Copy link
              </>
            )}
          </DropdownMenu.Item>
        </ul>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

/** Renders the completed transaction presentation without owning checkout state. */
export const CompletedTransactionView = ({ onDone }: { onDone: () => void }) => {
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const isMobile = useMedia(breakpoints.mobile, false);
  const currentTransaction = useTransactionStore((state) => state.currentTransaction);
  const transactions = useTransactionStore((state) => state.transactions);
  const [shouldShowShareModal, setShouldShowShareModal] = useState(false);
  const [isAddingTriggerOrders, setIsAddingTriggerOrders] = useState(false);
  const { triggerTransactionConfetti } = useTransactionConfetti();

  const currentTx = currentTransaction?.tx;
  const tx =
    currentTransaction && currentTx?.hash
      ? (transactions[currentTransaction.address]?.[currentTransaction.chainId]?.find(
          (transaction) =>
            transaction.hash === currentTx.hash || transaction.originalHash === currentTx.hash,
        ) ?? currentTx)
      : currentTx;
  const sellToken = tx?.sellToken;
  const buyToken = tx?.buyToken;
  const txHash = tx ? (getTxHash(tx) ?? (tx.hash as ChainHash)) : undefined;
  const chainId = sellToken?.chainId;
  const connectedAddress =
    buyToken?.chainId === CHAIN_IDS.SOLANA ? solanaWallet?.address : ethereumWallet?.address;
  const isTriggerOrderEligible =
    !!tx &&
    isPostSwapTriggerOrderEligible(tx) &&
    isSameTokenAddress(tx.recipient ?? currentTransaction?.address, connectedAddress);
  const triggerOrderSellAmount = usePostSwapSellAmount({
    tx,
    address: connectedAddress,
    enabled: isTriggerOrderEligible,
  });

  useEffect(() => {
    if (!txHash || !sellToken || !buyToken) return;
    triggerTransactionConfetti({ txHash, sellToken, buyToken });
  }, [buyToken, sellToken, triggerTransactionConfetti, txHash]);

  if (!tx || !sellToken || !buyToken) {
    return null;
  }

  const formattedSellAmount = tokenFormatter.format(tx.sellAmount, {
    decimals: sellToken.decimals,
    precision: 6,
  });
  const formattedBuyAmount = tokenFormatter.format(tx.buyAmount, {
    decimals: buyToken.decimals,
    precision: 6,
  });

  const onShareOnX = () => txHash && chainId && shareTxOnX(chainId, txHash);
  const onCopyLink = () => txHash && chainId && copyTxLinkInClipboard(chainId, txHash);

  const onShare = () => {
    if (!txHash) return;
    setShouldShowShareModal(true);
  };

  const canAddTriggerOrders = isTriggerOrderEligible && Boolean(triggerOrderSellAmount);
  const showTriggerOrders = isAddingTriggerOrders && canAddTriggerOrders;
  const triggerOrders: TriggerOrdersProps = {
    chainId: buyToken.chainId,
    displaySellAmount: formattedBuyAmount,
    onCancel: (hasPlacedOrder) => (hasPlacedOrder ? onDone() : setIsAddingTriggerOrders(false)),
    referenceTimestamp: Math.floor(new Date(tx.timestamp).getTime() / 1000),
    sellAmount: triggerOrderSellAmount,
    sellTokenAddress: buyToken.address,
    uiLocation: 'post_swap',
  };

  return (
    <div className="relative flex flex-col justify-between size-full overflow-y-auto overflow-x-hidden">
      {showTriggerOrders ? (
        <TriggerOrders {...triggerOrders} />
      ) : (
        <div className="pt-10 px-6">
          <div className="text-center mb-auto tablet:mb-[72px] desktop:mb-auto">
            <AlertSuccessGradient />
            <ModalBadge
              variant="success"
              className={classNames('relative mx-auto mb-7', MOVE_DOWN)}
            />
            <div>
              <div className={fontStyles({ variant: 'H5', class: ['mb-2'] })}>
                Transaction completed!
              </div>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['mb-8 text-token-gray-800 opacity-0 max-h-0', FADE_IN_AND_GROW],
                })}
              >
                {chainId === CHAIN_IDS.SOLANA && txHash ? (
                  <>
                    Your swap was successful, you can see your transaction on{' '}
                    <a
                      className={linkStyles({ color: 'purple', underline: true })}
                      href={getBlockExplorerTransactionURL(chainId, txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Solscan
                    </a>
                  </>
                ) : chainId && txHash ? (
                  <>
                    You can see more details in{' '}
                    <Link
                      className={linkStyles({ color: 'purple', underline: true })}
                      href={{
                        pathname: routes.TX,
                        query: { chainId: String(chainId), txHash },
                      }}
                    >
                      Trade History
                    </Link>
                  </>
                ) : (
                  'You can see more details in Trade History'
                )}
              </div>

              <div
                className={classNames(
                  'mt-4 mb-4 opacity-0 max-h-5 flex flex-col justify-center items-center',
                  FADE_IN_AND_GROW,
                )}
              >
                <div className="flex items-center justify-center">
                  <TokenAvatar
                    address={sellToken.address}
                    chainId={sellToken.chainId}
                    alt={`${sellToken.symbol} logo`}
                    size={32}
                    showChainLogo={false}
                  />
                  <div
                    className={fontStyles({
                      variant: 'BODY_LG',
                      class: ['ml-1.5', 'font-medium'],
                    })}
                  >
                    {`${formattedSellAmount} ${sellToken.symbol}`}
                  </div>
                </div>
                <ArrowDownIcon className="text-token-gray-600 my-2" />
                <div className="flex items-center justify-center">
                  <TokenAvatar
                    address={buyToken.address}
                    chainId={buyToken.chainId}
                    alt={`${buyToken.symbol} logo`}
                    size={32}
                    showChainLogo={false}
                  />
                  <div
                    className={fontStyles({
                      variant: 'BODY_LG',
                      class: ['font-medium', 'ml-1.5'],
                    })}
                  >
                    {`${formattedBuyAmount} ${buyToken.symbol}`}
                  </div>
                </div>
              </div>
              {canAddTriggerOrders && (
                <div className="mb-8">
                  <button
                    className={linkStyles({
                      color: 'purple',
                      underline: true,
                      font: 'LABEL_MD',
                    })}
                    onClick={() => {
                      ampli.addTpSlClicked({ ui_location: 'post_swap' });
                      setIsAddingTriggerOrders(true);
                    }}
                  >
                    Add TP/SL
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!showTriggerOrders && (
        <div className="flex w-full gap-2.5 pt-4 desktop:pt-0 pb-4 desktop:pb-6 px-2 desktop:px-6">
          {chainId && txHash && chainId !== CHAIN_IDS.SOLANA && (
            <>
              {isMobile ? (
                <>
                  <button
                    className={buttons({
                      size: 'large',
                      color: 'gray',
                      shape: 'pill',
                      class: ['flex-1', FADE_IN],
                    })}
                    onClick={onShare}
                  >
                    Share
                  </button>
                  <ShareTxBottomPanelModal
                    isOpen={shouldShowShareModal}
                    handleOpenChange={setShouldShowShareModal}
                    onShareOnX={onShareOnX}
                    url={getSharePageURL(chainId, txHash)}
                  />
                </>
              ) : (
                <ShareTxDropdown
                  trigger={
                    <button
                      className={buttons({
                        size: 'large',
                        color: 'gray',
                        shape: 'pill',
                        class: ['flex-1', FADE_IN],
                      })}
                      onClick={onShare}
                    >
                      Share
                    </button>
                  }
                  isOpen={shouldShowShareModal}
                  handleOpenChange={setShouldShowShareModal}
                  onShareOnX={onShareOnX}
                  onCopyLink={onCopyLink}
                />
              )}
            </>
          )}

          <button
            className={buttons({
              size: 'large',
              color: 'black',
              shape: 'pill',
              class: ['flex-1', FADE_IN],
            })}
            onClick={onDone}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};
